// "10-minute wow" first-touch onboarding.
//
// Shown to brand-new operators ONCE (gated by localStorage key
// 'crm:wow_onboarding:v1'). Three steps:
//
//   1. Upload last commission statement (CSV; PDF later).
//   2. AI computes "you're leaving $X on the table" + top 12 opps.
//   3. One-click: "have my AI text all 12 right now" — uses existing
//      SMS infra; mirrors them into leads + transactions for follow-up.
//
// Boring stuff (EIN, A2P, brand registration) happens in the
// background via the AI Setup Agent — operator never has to think
// about it until after the wow.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Upload, Zap, MessageSquare, X, CheckCircle, ArrowRight, AlertTriangle, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';

const STORAGE_KEY = 'crm:wow_onboarding:v1';

export function shouldShowWowOnboarding() {
  try { return localStorage.getItem(STORAGE_KEY) !== 'completed' && localStorage.getItem(STORAGE_KEY) !== 'dismissed'; }
  catch { return true; }
}

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

// Minimal generic CSV parser, mirrors api/_credits-side parseGenericCsv
// shape. Lifted here so the wow flow doesn't depend on internal CT.jsx.
function parseGenericCsv(text) {
  const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const parseLine = (l) => {
    const out = []; let cur = ''; let inQuote = false;
    for (let i = 0; i < l.length; i++) {
      const c = l[i];
      if (c === '"' && l[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQuote = !inQuote;
      else if (c === ',' && !inQuote) { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur);
    return out.map(s => s.trim());
  };
  const header = parseLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g, '_'));
  const idx = (k) => header.findIndex(h => h === k || h.includes(k));
  const map = {
    client_name:      idx('client_name') >= 0 ? idx('client_name') : idx('name'),
    client_phone:     idx('client_phone') >= 0 ? idx('client_phone') : idx('phone'),
    client_email:     idx('client_email') >= 0 ? idx('client_email') : idx('email'),
    carrier:          idx('carrier'),
    plan_name:        idx('plan_name') >= 0 ? idx('plan_name') : idx('plan'),
    plan_type:        idx('plan_type'),
    effective_date:   idx('effective_date') >= 0 ? idx('effective_date') : idx('effective'),
    renewal_date:     idx('renewal_date') >= 0 ? idx('renewal_date') : idx('renewal'),
    termination_date: idx('termination_date') >= 0 ? idx('termination_date') : idx('termination'),
    monthly_premium:  idx('monthly_premium') >= 0 ? idx('monthly_premium') : idx('premium'),
    monthly_commission: idx('monthly_commission') >= 0 ? idx('monthly_commission') : idx('commission'),
    status:           idx('status'),
  };
  return lines.slice(1).map(line => {
    const cells = parseLine(line);
    const get = (k) => (map[k] >= 0 ? cells[map[k]] : '') || '';
    return {
      client_name:        get('client_name'),
      client_phone:       get('client_phone'),
      client_email:       get('client_email'),
      carrier:            get('carrier'),
      plan_name:          get('plan_name'),
      plan_type:          get('plan_type'),
      effective_date:     get('effective_date'),
      renewal_date:       get('renewal_date'),
      termination_date:   get('termination_date'),
      monthly_premium:    get('monthly_premium'),
      monthly_commission: get('monthly_commission'),
      status:             (get('status') || 'active').toLowerCase(),
    };
  }).filter(r => r.client_name);
}

export default function WowOnboarding({ onComplete, onDismiss, showToast = () => {} }) {
  const [step, setStep]       = React.useState(1);
  const [rows, setRows]       = React.useState([]);
  const [audit, setAudit]     = React.useState(null);
  const [selected, setSelected] = React.useState(new Set());
  const [busy, setBusy]       = React.useState(false);
  const [blast, setBlast]     = React.useState(null);
  const fileRef = React.useRef(null);

  const dismiss = () => { try { localStorage.setItem(STORAGE_KEY, 'dismissed'); } catch {} onDismiss?.(); };
  const finish  = () => { try { localStorage.setItem(STORAGE_KEY, 'completed'); } catch {} onComplete?.(); };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseGenericCsv(text);
      if (parsed.length === 0) { showToast('No rows found in that file. Make sure it has a header row with client_name + renewal_date columns.'); return; }
      setRows(parsed);
      setBusy(true);
      const a = await apiFetch('/api/onboarding/audit', { method: 'POST', body: JSON.stringify({ rows: parsed }) });
      setAudit(a);
      // Pre-select every high-urgency opportunity — by its index in the FULL
      // list (the old .filter().map((_,i)=>i) produced filtered-array indices,
      // which selected the wrong rows).
      const opps = Array.isArray(a?.top_opportunities) ? a.top_opportunities : [];
      setSelected(new Set(opps.map((o, i) => (o.urgency === 'high' ? i : -1)).filter(i => i >= 0)));
      setStep(2);
    } catch (err) { showToast(`Audit failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text && text.includes(',')) {
      e.preventDefault();
      const file = new File([text], 'pasted.csv', { type: 'text/csv' });
      handleFile(file);
    }
  };

  const toggleSelected = (i) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };

  const sendBlast = async () => {
    if (selected.size === 0) { showToast('Pick at least one client to text.'); return; }
    setBusy(true);
    try {
      const ops = Array.from(selected).map(i => audit.top_opportunities[i]).filter(Boolean);
      const r = await apiFetch('/api/onboarding/blast-renewals', { method: 'POST', body: JSON.stringify({ opportunities: ops }) });
      setBlast(r);
      setStep(3);
    } catch (err) { showToast(`Blast failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-4" onClick={dismiss}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-slate-900 to-emerald-900 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Welcome · 10-minute setup</div>
            <h2 className="text-lg font-bold mt-0.5">
              {step === 1 && 'Show me where the money is hiding'}
              {step === 2 && ((audit?.top_opportunities?.length || 0) > 0 ? audit?.missed_money_label : 'Your book is imported')}
              {step === 3 && 'You just rebooked your book'}
            </h2>
          </div>
          <button onClick={dismiss} className="text-slate-300 hover:text-white"><X size={20}/></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Drop your last commission statement (or any client export). In 30 seconds the AI will show you exactly which renewals you're about to lose — and offer to text them all for you.</p>
              <div
                onClick={() => fileRef.current?.click()}
                onPaste={handlePaste}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl p-8 text-center cursor-pointer transition-colors"
              >
                <Upload size={32} className="mx-auto text-slate-400 mb-2"/>
                <div className="font-bold text-slate-900">{busy ? 'Analyzing…' : 'Drop CSV here, click to browse, or paste'}</div>
                <div className="text-xs text-slate-500 mt-1">Carrier export, your own spreadsheet, or any list with name + phone + renewal date columns.</div>
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}/>
              </div>
              <div className="text-xs text-slate-500 text-center">
                Or <button onClick={dismiss} className="underline hover:text-slate-900">skip and explore the empty CRM</button> — you can always come back.
              </div>
            </div>
          )}

          {step === 2 && audit && (() => {
            const opps = Array.isArray(audit.top_opportunities) ? audit.top_opportunities : [];
            const hasOpps = opps.length > 0;
            const imported = audit.stats?.total_clients ?? rows.length;
            return (
            <div className="space-y-4">
              {/* Import success is the HERO — a valid book with no current risk
                  is a good outcome, not a failure. Value-at-risk is a small
                  positive insight beneath it. */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 text-center">
                <CheckCircle size={30} className="mx-auto text-emerald-600 mb-1"/>
                <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Book imported successfully</div>
                <div className="text-5xl font-black text-emerald-900 mt-1">{imported}</div>
                <div className="text-sm text-emerald-900/80 mt-1">client{imported === 1 ? '' : 's'} added to your book</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Clients"        value={audit.stats?.total_clients ?? imported}/>
                <Stat label="Expiring 30d"    value={audit.stats?.expiring_30d ?? 0}/>
                <Stat label="Recently lapsed" value={audit.stats?.recently_lapsed ?? 0}/>
              </div>

              {hasOpps ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <span className="text-sm text-amber-900"><b>{audit.missed_money_label}</b> in annualized commission is at risk across {opps.length} client{opps.length === 1 ? '' : 's'} most likely to slip this week.</span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-sm">Pick who the AI texts right now</div>
                      <div className="text-xs text-slate-500">{selected.size} of {opps.length} selected</div>
                    </div>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                      {opps.map((o, i) => (
                        <label key={i} className="flex items-start gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelected(i)} className="mt-1"/>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="font-semibold text-slate-900 text-sm">{o.client_name}</span>
                              <span className={`text-[10px] font-bold uppercase border rounded px-1.5 py-0.5 ${o.urgency === 'high' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{o.urgency}</span>
                            </div>
                            <div className="text-xs text-slate-500">{o.reason}</div>
                            <div className="text-[11px] text-slate-600 italic mt-1 line-clamp-2">"{o.suggested_message}"</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Primary action only exists when there's something to act on. */}
                  <button onClick={sendBlast} disabled={busy || selected.size === 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                    <Sparkles size={16}/>
                    {busy ? 'Sending…' : selected.size > 0 ? `Have AI text these ${selected.size} now` : 'Select clients to text'}
                  </button>
                  <button onClick={finish} className="w-full text-slate-500 hover:text-slate-900 text-sm font-semibold py-1">
                    Skip texting for now — continue setup →
                  </button>
                  <div className="text-xs text-slate-500 text-center">
                    Texts go out from your dedicated number once A2P clears (auto, ~24h). Until then, they ride the platform shared brand so they land today.
                  </div>
                </>
              ) : (
                // Zero actionable clients = a clean book. Success, not a dead end.
                <>
                  <div className="text-sm text-slate-600 text-center px-2">
                    We analyzed your book and don't see any immediate lapse or expiration risks right now. That's a healthy book — we'll keep watching and flag renewals as they approach.
                  </div>
                  <button onClick={finish}
                    className="w-full bg-slate-900 hover:bg-black text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                    Continue setup <ArrowRight size={16}/>
                  </button>
                  <button onClick={finish} className="w-full text-slate-500 hover:text-slate-900 text-sm font-semibold py-1">
                    View my clients
                  </button>
                </>
              )}
            </div>
            );
          })()}

          {step === 3 && blast && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 text-center">
                <CheckCircle size={36} className="mx-auto text-emerald-600 mb-2"/>
                <div className="text-2xl font-black text-emerald-900">{blast.sent + blast.queued} texts on the way</div>
                <div className="text-sm text-emerald-800/80 mt-1">{blast.failed > 0 && `${blast.failed} skipped (bad phone). `}You can watch replies land in the Leads tab.</div>
              </div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {blast.results.map((r, i) => (
                  <div key={i} className="px-3 py-1.5 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-900">{r.name}</span>
                    <span className={`text-[10px] font-bold uppercase border rounded px-1.5 py-0.5 ${
                      r.status === 'sent' || r.status === 'queued' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      r.status === 'dry_run' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                      'bg-slate-50 text-slate-600 border-slate-200'
                    }`}>{r.dry_run ? 'queued (dry-run)' : r.status}</span>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-2">
                <div className="font-bold text-slate-900">What's happening in the background:</div>
                <div className="flex items-start gap-2"><Zap size={14} className="text-violet-600 flex-shrink-0 mt-0.5"/><span>The AI Setup Agent is provisioning your dedicated texting number + submitting your A2P brand. You don't have to lift a finger.</span></div>
                <div className="flex items-start gap-2"><MessageSquare size={14} className="text-violet-600 flex-shrink-0 mt-0.5"/><span>When clients reply, your AI Setter agent takes the conversation and books appointments to your calendar.</span></div>
              </div>
              <button onClick={finish} className="w-full bg-slate-900 hover:bg-black text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                Take me to the CRM <ArrowRight size={16}/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
